// A minimal ZIP writer. Entries are stored uncompressed, which keeps this small
// enough to own outright rather than taking on a compression dependency.

export type ZipEntry = {
  name: string;
  text: string;
};

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let value = i;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[i] = value >>> 0;
  }
  return table;
})();

export function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc = (CRC_TABLE[(crc ^ byte) & 0xff] ?? 0) ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

const LOCAL_HEADER_SIZE = 30;
const CENTRAL_HEADER_SIZE = 46;
const EOCD_SIZE = 22;
const UTF8_NAME_FLAG = 0x0800;
const VERSION_STORED = 20;

function dosTime(when: Date): number {
  return (when.getHours() << 11) | (when.getMinutes() << 5) | (when.getSeconds() >> 1);
}

function dosDate(when: Date): number {
  return ((when.getFullYear() - 1980) << 9) | ((when.getMonth() + 1) << 5) | when.getDate();
}

type Prepared = {
  name: Uint8Array;
  body: Uint8Array;
  crc: number;
  offset: number;
};

export function buildZip(entries: ZipEntry[], modified: Date = new Date()): Uint8Array {
  const encoder = new TextEncoder();
  const time = dosTime(modified);
  const date = dosDate(modified);

  const prepared: Prepared[] = [];
  let offset = 0;
  for (const entry of entries) {
    const name = encoder.encode(entry.name);
    const body = encoder.encode(entry.text);
    prepared.push({ name, body, crc: crc32(body), offset });
    offset += LOCAL_HEADER_SIZE + name.length + body.length;
  }

  const centralSize = prepared.reduce(
    (total, entry) => total + CENTRAL_HEADER_SIZE + entry.name.length,
    0,
  );
  const bytes = new Uint8Array(offset + centralSize + EOCD_SIZE);
  const view = new DataView(bytes.buffer);

  for (const entry of prepared) {
    let at = entry.offset;
    view.setUint32(at, 0x04034b50, true);
    view.setUint16(at + 4, VERSION_STORED, true);
    view.setUint16(at + 6, UTF8_NAME_FLAG, true);
    view.setUint16(at + 8, 0, true); // stored, no compression
    view.setUint16(at + 10, time, true);
    view.setUint16(at + 12, date, true);
    view.setUint32(at + 14, entry.crc, true);
    view.setUint32(at + 18, entry.body.length, true);
    view.setUint32(at + 22, entry.body.length, true);
    view.setUint16(at + 26, entry.name.length, true);
    view.setUint16(at + 28, 0, true); // no extra field
    at += LOCAL_HEADER_SIZE;
    bytes.set(entry.name, at);
    bytes.set(entry.body, at + entry.name.length);
  }

  let at = offset;
  for (const entry of prepared) {
    view.setUint32(at, 0x02014b50, true);
    view.setUint16(at + 4, VERSION_STORED, true);
    view.setUint16(at + 6, VERSION_STORED, true);
    view.setUint16(at + 8, UTF8_NAME_FLAG, true);
    view.setUint16(at + 10, 0, true);
    view.setUint16(at + 12, time, true);
    view.setUint16(at + 14, date, true);
    view.setUint32(at + 16, entry.crc, true);
    view.setUint32(at + 20, entry.body.length, true);
    view.setUint32(at + 24, entry.body.length, true);
    view.setUint16(at + 28, entry.name.length, true);
    view.setUint16(at + 30, 0, true); // no extra field
    view.setUint16(at + 32, 0, true); // no comment
    view.setUint16(at + 34, 0, true); // disk number
    view.setUint16(at + 36, 0, true); // internal attributes
    view.setUint32(at + 38, 0, true); // external attributes
    view.setUint32(at + 42, entry.offset, true);
    bytes.set(entry.name, at + CENTRAL_HEADER_SIZE);
    at += CENTRAL_HEADER_SIZE + entry.name.length;
  }

  view.setUint32(at, 0x06054b50, true);
  view.setUint16(at + 4, 0, true); // disk number
  view.setUint16(at + 6, 0, true); // disk with the central directory
  view.setUint16(at + 8, prepared.length, true);
  view.setUint16(at + 10, prepared.length, true);
  view.setUint32(at + 12, centralSize, true);
  view.setUint32(at + 16, offset, true);
  view.setUint16(at + 20, 0, true); // no archive comment

  return bytes;
}
